/**
 * Pure, React-free helpers for the Team domain: id generation, blank-entity
 * factories, validation, member normalization, the people-directory aggregation
 * used by pickers, and sorting.
 */

import { v4 as uuid } from 'uuid';
import type {
  DirectoryPerson,
  Team,
  TeamMember,
  TeamSummary,
} from './types';

/** Deterministic-ish document id for a new team. */
export function teamDocId(): string {
  return `team_${uuid()}`;
}

/** Creates a blank member row for the editor. */
export function blankMember(): TeamMember {
  return { name: '', alias: '', role: '', email: '' };
}

/** Creates a new, empty team shell for the create form. */
export function blankTeam(): Team {
  const now = new Date().toISOString();
  return {
    id: '',
    name: '',
    description: '',
    members: [],
    createdBy: '',
    createdAt: now,
    lastUpdated: now,
    deleted: false,
  };
}

/** Trims a member's fields; returns a normalized copy. */
export function normalizeMember(m: TeamMember): TeamMember {
  return {
    name: (m.name ?? '').trim(),
    alias: (m.alias ?? '').trim(),
    role: (m.role ?? '').trim(),
    email: (m.email ?? '').trim(),
  };
}

/** Drops empty rows (no alias AND no name) and normalizes the rest. */
export function cleanMembers(members: TeamMember[]): TeamMember[] {
  return (members ?? [])
    .map(normalizeMember)
    .filter((m) => m.alias || m.name);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates a team for save. Returns a list of human-readable problems; an
 * empty list means the team is valid.
 */
export function validateTeam(team: Pick<Team, 'name' | 'members'>): string[] {
  const problems: string[] = [];
  if (!team.name.trim()) problems.push('Team name is required.');

  const seen = new Set<string>();
  const members = cleanMembers(team.members);
  members.forEach((m, i) => {
    const n = i + 1;
    if (!m.alias) problems.push(`Member ${n}: alias is required.`);
    if (!m.name) problems.push(`Member ${n}: name is required.`);
    if (m.email && !EMAIL_RE.test(m.email)) {
      problems.push(`Member ${n}: "${m.email}" is not a valid email.`);
    }
    if (m.alias) {
      const key = m.alias.toLowerCase();
      if (seen.has(key)) problems.push(`Member ${n}: duplicate alias "${m.alias}".`);
      seen.add(key);
    }
  });
  return problems;
}

/** Sorts team summaries newest-first by creation time. */
export function sortTeamsByRecency(summaries: TeamSummary[]): TeamSummary[] {
  return summaries
    .slice()
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

/**
 * Aggregates all team members into a deduped, alias-keyed people directory for
 * the app's people pickers. Later teams enrich (but never overwrite) an
 * existing entry's name/email/role; every team the alias appears in is recorded.
 */
export function buildPeopleDirectory(teams: TeamSummary[]): DirectoryPerson[] {
  const byAlias = new Map<string, DirectoryPerson>();
  for (const team of teams) {
    for (const raw of team.members ?? []) {
      const m = normalizeMember(raw);
      if (!m.alias) continue;
      const key = m.alias.toLowerCase();
      const existing = byAlias.get(key);
      if (existing) {
        if (!existing.name && m.name) existing.name = m.name;
        if (!existing.email && m.email) existing.email = m.email;
        if (!existing.role && m.role) existing.role = m.role;
        if (team.name && !existing.teams.includes(team.name)) {
          existing.teams.push(team.name);
        }
      } else {
        byAlias.set(key, {
          alias: m.alias,
          name: m.name,
          email: m.email,
          role: m.role,
          teams: team.name ? [team.name] : [],
        });
      }
    }
  }
  return Array.from(byAlias.values()).sort((a, b) =>
    (a.name || a.alias).localeCompare(b.name || b.alias),
  );
}
